import Dinero, { Currency } from "dinero.js";
import detectBrowserLanguage from "detect-browser-language";

const currentLan = detectBrowserLanguage();
Dinero.globalLocale = currentLan;

type DineroInstance = Dinero.Dinero;

type SupportedCurrencies = "EUR" | "MXN";

export interface MoneyConstructorArgs {
  amountInCents: number;
  currency?: SupportedCurrencies;
}

export class Money {
  amountInCents: number;

  currency: string;

  constructor({ amountInCents = 0, currency = "EUR" }: MoneyConstructorArgs) {
    this.amountInCents = amountInCents;
    this.currency = currency;
  }

  static zero(currency?: "EUR" | "MXN") {
    return new Money({
      amountInCents: 0,
      currency,
    });
  }

  getCurrency() {
    return this.toDineroInstance().getCurrency();
  }

  add(money: Money): Money {
    return Money.fromDineroInstance(
      this.toDineroInstance().add(money.toDineroInstance())
    );
  }

  equal(money: Money) {
    return this.toDineroInstance().equalsTo(money.toDineroInstance());
  }

  greaterThan(money: Money) {
    return this.toDineroInstance().greaterThan(money.toDineroInstance());
  }

  greaterThanOrEqual(money: Money) {
    return this.toDineroInstance().greaterThanOrEqual(money.toDineroInstance());
  }

  toUnitary(): number {
    return this.toDineroInstance().toUnit();
  }

  toString(format = "$0,0.0"): string {
    return this.toDineroInstance().toFormat(format);
  }

  private toDineroInstance(): DineroInstance {
    return Dinero({
      amount: this.amountInCents,
      currency: this.currency?.toUpperCase() as Currency,
      precision: 2,
    });
  }

  private static fromDineroInstance(instance: DineroInstance): Money {
    return new Money({
      amountInCents: instance.getAmount(),
      currency: instance.getCurrency() as SupportedCurrencies,
    });
  }
}
